<template>
  <div>
    <div class="row">
      <div class="col-12 col-md-6">
        <!-- documentData carries `establishment` so the contacts picker suggests the
             contacts of the PRODUCTION establishment, which is the addressee here. -->
        <documents-send-mail-form
          :documentData="mailDocument"
          :dispatchUrl="'establishmentInternalOrders/sendMail'"
          :toggleMailModal="toggleMailModal"
          :mailInformation="mailInformation"
        />
      </div>

      <div class="col-12 col-md-6">
        <h3 class="mb-3">{{ order.seq_number || order.code }}</h3>

        <dl class="row mb-2">
          <dt class="col-sm-5">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK") }}
          </dt>
          <dd class="col-sm-7">
            {{ order.week_starts_at }} &rarr; {{ order.week_ends_at }}
          </dd>
        </dl>
        <dl class="row mb-2">
          <dt class="col-sm-5">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT") }}
          </dt>
          <dd class="col-sm-7">
            {{ order.sourceEstablishment && order.sourceEstablishment.company_name }}
          </dd>
        </dl>
        <dl class="row mb-2">
          <dt class="col-sm-5">
            {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SATELLITE_ESTABLISHMENT") }}
          </dt>
          <dd class="col-sm-7">
            {{
              order.destinationEstablishment &&
              order.destinationEstablishment.company_name
            }}
          </dd>
        </dl>

        <!-- Day totals rather than the whole monday→friday grid: the grid is far too
             wide to be readable inside a modal. -->
        <table class="table table-sm mt-3">
          <thead class="thead-light">
            <tr>
              <th>{{ $t("COMMON.DATE") }}</th>
              <th class="text-right">{{ $t("COMMON.QUANTITY") }}</th>
              <th class="text-right">{{ $t("COMMON.TOTAL") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in dayRows" :key="day.date">
              <td>{{ day.label }}</td>
              <td class="text-right">{{ day.quantity }}</td>
              <td class="text-right">{{ $formatCurrency(day.total) }}</td>
            </tr>
            <tr v-if="!dayRows.length">
              <td colspan="3" class="text-center text-muted">
                {{ $t("COMMON.NO_DATA") }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="dayRows.length">
            <tr>
              <th>{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK_TOTAL") }}</th>
              <th class="text-right">{{ totalQuantity }}</th>
              <th class="text-right">
                {{ $formatCurrency(order.pricing ? order.pricing.total || 0 : 0) }}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div class="d-flex justify-content-end">
      <el-button type="danger" @click="toggleMailModal">
        {{ $t("COMMON.CANCEL") }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { Button } from "element-ui";
import moment from "moment";
import { formatNumber, parseNumber } from "libphonenumber-js";
import DocumentsSendMailForm from "@/components/DocumentsSendMailForm";

const baseAppUrl = process.env.VUE_APP_BASE_URL;

export default {
  name: "establishment-internal-order-mail-modal-content",

  components: {
    DocumentsSendMailForm,
    [Button.name]: Button,
  },

  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
    toggleMailModal: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      mailInformation: {
        organization: {},
        recipient: {},
      },
    };
  },

  computed: {
    /**
     * The generic mail form resolves its contacts from `documentData.establishment`.
     * For an internal order the addressee is the production establishment.
     */
    mailDocument() {
      return {
        ...this.order,
        establishment: this.order.sourceEstablishment,
      };
    },
    dayRows() {
      const byDay = this.order.value_breakdown?.by_day || {};
      return Object.keys(byDay)
        .sort()
        .map((date) => ({
          date,
          label: moment(date).format("ddd DD/MM"),
          quantity: byDay[date].quantity,
          total: byDay[date].total,
        }));
    },
    totalQuantity() {
      return this.dayRows.reduce((carry, day) => carry + Number(day.quantity || 0), 0);
    },
  },

  mounted() {
    this.refreshMailInformations();
  },

  watch: {
    order() {
      this.refreshMailInformations();
    },
  },

  methods: {
    formatPhoneNumber(phoneNumber) {
      try {
        return formatNumber(parseNumber(phoneNumber), "NATIONAL");
      } catch (e) {
        return phoneNumber;
      }
    },

    refreshMailInformations() {
      const user = this.$store.getters["profile/me"] || {};
      const production = this.order.sourceEstablishment || {};

      const route = this.$router.resolve({
        name: "View EstablishmentInternalOrder",
        params: { id: this.order.id },
      });

      this.mailInformation = {
        organization: this.order.organization,
        subject: this.$t("COMMON.EMAIL_INFOS_SUBJECT_INTERNAL_ORDER", {
          organization: this.order.organization?.name || "",
          seq_number: this.order.seq_number || this.order.code || "",
        }),
        recipient: {
          username: production.company_name,
          // The establishment mailbox, never `billing_email`: that one is the
          // client's billing address, which has no business receiving an
          // internal document.
          email: production.email,
        },
        body: this.$t("COMMON.INTERNAL_ORDER_EMAIL_INFOS_BODY", {
          username: production.company_name || "",
          satellite: this.order.destinationEstablishment?.company_name || "",
          week_starts_at: this.order.week_starts_at
            ? this.$formatDate(this.order.week_starts_at, "LL")
            : "",
          week_ends_at: this.order.week_ends_at
            ? this.$formatDate(this.order.week_ends_at, "LL")
            : "",
          sender_name: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
          phone: user.phone
            ? `${
                user.phone_type
                  ? this.$t(`COMMON.PHONE_TYPE_${user.phone_type}`) + ":"
                  : ""
              } ${this.formatPhoneNumber(user.phone)}${
                user.phone_extension ? ` (ext: ${user.phone_extension})` : ""
              }`
            : "",
          email: user.email || "",
          organization: this.order.organization?.name || "",
          link: `
            <p>
              <a
                href="${baseAppUrl + route.href}"
                rel="noopener noreferrer" target="_blank"
                class="button"
              >
                ${this.$t("COMMON.VALIDATE_INTERNAL_ORDER")}
              </a>
            </p>
          `,
        }),
      };
    },
  },
};
</script>
